import { EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import {
  fetchData,
  urls,
} from '@internal/backstage-plugin-dh-dock-shared-common';
import {
  Avatar,
  Badge,
  Card,
  CardContent,
  CardHeader,
  Divider,
  makeStyles,
} from '@material-ui/core';
import Security from '@material-ui/icons/Security';
import VerifiedUser from '@material-ui/icons/VerifiedUser';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import useCustomEntities from '../../../hooks/useEntities';
import { LinearProgress } from '@material-ui/core';
import { setTeamType } from '../../../redux/slices/globalVariableSlice';
import { useDispatch } from 'react-redux';
export type MemberType = 'contributor' | 'core' | 'champions';
export type TeamTopologyStatus = 'defined' | 'missing';
type ChampionType =
  | 'DevOps Champion'
  | 'Security Champion'
  | 'DevOps & Security Champion'
  | 'Qa Champion'
  | 'Architecture Champion'
  | 'Operations Champion'
  | 'none'
  | undefined;
type DataSource = 'People App' | 'Active Directory' | ' Both ';
export interface MemberListProps {
  targetRef: string;
  type: string;
  role?: string;
  source?: DataSource;
  combinedSource?: DataSource;
}

interface UserRoleProps {
  link: string;
  role: string;
}
interface DisplayCardProps {
  displayName: string;
  picture: string;
  value: Entity | undefined;
  role: string;
  type: Team['type'];
}
type Team = {
  link?: string;
  role?: string | ChampionType;
  targetRef: string;
  champion?: ChampionType;
  type: 'core' | 'contributor' | 'champions';
  source?: DataSource;
  sources?: string[];
};
type LegendProps = {
  dataSource: DataSource;
};
interface ChampsProps {
  targetRef: string;
  role?: ChampionType;
}

const normalizeMemberRef = (targetRef: string) =>
  targetRef
    ?.toLowerCase()
    ?.replace(/^user:default\//, '')
    ?.replace(/^group:default\//, '')
    ?.replaceAll('@', '_');

const resolveMemberEntity = (targetRef: string, users: Entity[]) => {
  const normalizedTarget = normalizeMemberRef(targetRef);

  return users?.find(user => {
    const typedUser = user as any;
    const name = typedUser?.metadata?.name?.toLowerCase();
    const email = typedUser?.spec?.profile?.email?.toLowerCase();
    const displayName = typedUser?.spec?.profile?.displayName?.toLowerCase();

    return [name, email, displayName].some(value => {
      if (!value) {
        return false;
      }

      return (
        value === normalizedTarget ||
        value.replace(/@/g, '_') === normalizedTarget ||
        value.replace(/^user:default\//, '') === normalizedTarget
      );
    });
  });
};

const useStyles = makeStyles({
  card: {
    display: 'flex',
    minHeight: '50px',
    height: 'auto',
  },
  emptyCard: {
    marginLeft: 15,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    '& button': {
      height: '34px',
      lineHeight: '10px',
      marginTop: '16px',
      cursor: 'pointer',
    },
  },

  cardData: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    width: '220px', // fixed width for all cards
    minHeight: '150px', // fixed height
    padding: '16px',
    marginTop: '25px',
    boxSizing: 'border-box',
  },

  cardContent: {
    position: 'relative',
    display: 'flex',
    gap: '18px',
    flexWrap: 'wrap',
  },
  name: {
    fontSize: '15px',
    fontWeight: 700,
    lineHeight: '1.6',
    whiteSpace: 'nowrap',
    marginTop: '40px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  role: {
    color: '#05164d',
    position: 'relative',
    marginTop: '7px',
    bottom: 0,
  },
  avatar: {
    color: 'transparent',
    textAlign: 'center',
    position: 'absolute',
    height: '3em',
    width: '4rem',
    marginTop: '-150px',
  },
  container: {
    display: 'flex',
    justifyContent: 'end',
    top: '20px',
    float: 'right',
    textAlign: 'right',
    marginRight: '30px',
  },
  legendContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    padding: '10px',
    marginTop: '5px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.1)',
    backgroundColor: 'white',
  },
  legendItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  icon: {
    fontSize: '24px',
  },
  text: {
    fontSize: '14px',
    color: '#333',
  },
});

const Legend = ({ dataSource }: LegendProps) => {
  const classes = useStyles();

  const icons = [
    { Icon: VerifiedUser, label: 'ID-manager' },
    { Icon: Security, label: 'PeopleApp' },
  ];

  return (
    <div className={classes.container}>
      <div className={classes.legendContainer}>
        {icons.map(({ Icon, label }, index) => {
          const show =
            dataSource === ' Both ' ||
            (dataSource === 'Active Directory' && label === 'ID-manager')||(dataSource === 'People App' && label === 'PeopleApp');

          if (!show) return null;

          return (
            <div key={index} className={classes.legendItem}>
              <Icon className={classes.icon} />
              <span className={classes.text}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DisplayCard: FC<DisplayCardProps> = ({
  displayName,
  picture,
  value,
  role,
  type,
}) => {
  const classes = useStyles();
  
  return (
    <Card
      variant="elevation"
      className={classes.cardData}
      style={{ backgroundColor: value === undefined ? '#8080801C' : '#fff' }}
    >
      {type === 'champions' && (
        <Avatar alt={displayName} src={picture} className={classes.avatar} />
      )}
      {type === 'core' && (
        <Avatar alt={displayName} src={picture} className={classes.avatar} />
      )}
      <div
        style={{
          display: 'flex',
          gap: '50px',
          marginTop: '-60px',
          position: 'absolute',
        }}
      >
        {type === 'core' && (
          <>
            <Badge
              overlap="circular"
              badgeContent={<Security fontSize="small" />}
            />
          </>
        )}
      </div>
      {value && value?.metadata?.name ? (
        <EntityRefLink
          entityRef={{
            kind: value.kind ?? 'User',
            namespace: value.metadata.namespace ?? 'default',
            name: value.metadata.name,
          }}
          className={classes.name}
          title={displayName?.replace(',', '')?.toUpperCase()}
        />
      ) : (
        <p style={{ marginTop: '50px' }}>{displayName}</p>
      )}

      <label className={classes.role}>{role ?? ''}</label>
    </Card>
  );
};

function DisplayCardList({
  targetRef,
  role,
  champion,
  type,
  source,
  entityValue,
}: Team & { entityValue?: Entity }) {
  const classes = useStyles();
  const value = entityValue;

  if (value) {
    const {
      spec: { profile: { displayName = 'Unknown', picture = '' } = {} },
    } = value as any;

    if (type === 'champions') {
      
      return (
        <>
          <DisplayCard
            displayName={displayName}
            picture={picture}
            role={role ?? ''}
            value={value}
            type={type}
          />
        </>
      );
    }

    return (
      <Card variant="elevation" className={classes.cardData}>
        <Avatar alt={displayName} src={picture} className={classes.avatar} />
        <div
          style={{
            display: 'flex',
            gap: '50px',
            marginTop: '-60px',
            position: 'absolute',
          }}
        >
          {type === 'core' && source && source.includes('+') && (
            <>
              <Badge
                overlap="circular"
                badgeContent={<VerifiedUser fontSize="small" />}
              />
              <Badge
                overlap="circular"
                badgeContent={<Security fontSize="small" />}
              />
            </>
          )}

          {type === 'core' && source === 'Active Directory' && (
            <Badge
              overlap="circular"
              badgeContent={<VerifiedUser fontSize="small" />}
            />
          )}
          {type === 'core' && source === 'People App' && (
            <Badge
              overlap="circular"
              badgeContent={<Security fontSize="small" />}
            />
          )}
        </div>
        {value?.metadata?.name && (
          <EntityRefLink
            entityRef={{
              kind: value.kind ?? 'User',
              namespace: value.metadata.namespace ?? 'default',
              name: value.metadata.name,
            }}
            className={classes.name}
            title={displayName?.replace(',', '')?.toUpperCase()}
          />
        )}
        <label
          className={classes.role}
          style={{ visibility: !role ? 'hidden' : 'visible' }}
        >
          {role ?? 'role'}
        </label>

        <label
          className={classes.role}
          style={{
            visibility: champion === 'none' ? 'hidden' : 'visible',
            textAlign: 'center',
          }}
        >
          {champion}
        </label>
      </Card>
    );
  } else  {
    return (
      <DisplayCard
        displayName={targetRef?.replace('user:default/', '').replace('_', '@')}
        picture={
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADUlEQVR4nGNoaGj4DwAEAwH9X4pW3wAAAABJRU5ErkJggg=='
        }
        role={role ?? ''}
        value={undefined}
        type={type}
      />
    );
  }
}

const normalizeTargetRef = (member: string) => {
  if (!member?.toLowerCase().startsWith('user:default/')) {
    return 'user:default/'.concat(member);
  }
  return member;
};

const MembersList: React.FC<{
  name: string;
  memberType: MemberType;
  hasDataIsh?: (data?: boolean) => void;
  onTeamTopologyStatusChange?: (status: TeamTopologyStatus) => void;
}> = ({ name, memberType, hasDataIsh, onTeamTopologyStatusChange }) => {

  document.title = name;
  const classes = useStyles();
  const [groupContributorList, setGroupContributorList] = useState<
    MemberListProps[]
  >([]);
  const { entity } = useEntity();
  const groupInfo=entity?.relations?.find(relation => relation.type === 'hasGroupInfo')?.targetRef;
  const [coreMembers, setCoreMembers] = useState<MemberListProps[]>();
  const [roleList, setRoleList] = useState<UserRoleProps[]>([]);
  const [teamChampions, setTeamChampions] = useState<ChampsProps[]>([]);
  const [catalogUsers, setCatalogUsers] = useState<Entity[]>([]);
  const [loading, setLoading] = useState({
    isChampionsLoading: true,
    isMembersLoading: true,
  });
  const { getCustomEntitiesByKind } = useCustomEntities('User');
  const dispatch = useDispatch();
  
  // Redux selectors
 


  function hasSomeDataIssues(member:ChampsProps,userList:Entity[]){
     const m = member as { targetRef?: string };

            const normalizedTarget = m.targetRef
              ?.toLowerCase()
              ?.replace('user:default/', '')
              .replace('_', '@');

            const isFound = userList?.some(
              (user: any) =>
                user.spec?.profile?.email?.toLowerCase() === normalizedTarget,
            );
        return isFound    
  }
  async function handleChampions(processData: Entity,userList?:Entity[]) {
    if (processData?.spec) {
      const championslist: ChampsProps[] = [];
      const {
        architectureChampion,
        opsChampion,
        qaChampion,
        securityChampion,
        devopsChampion,
      } = processData?.spec as any;
      if (architectureChampion) {
        championslist.push({
          targetRef: normalizeTargetRef(architectureChampion) as string,
          role: 'Architecture Champion',
        });
      }
      if (opsChampion) {
        championslist.push({
          targetRef: normalizeTargetRef(opsChampion) as string,
          role: 'Operations Champion',
        });
      }
      if (qaChampion) {
        championslist.push({
          targetRef: normalizeTargetRef(qaChampion) as string,
          role: 'Qa Champion',
        });
      }
      if (securityChampion) {
        championslist.push({
          targetRef: normalizeTargetRef(securityChampion) as string,
          role: 'Security Champion',
        });
      }
      if (devopsChampion) {
        championslist.push({
          targetRef: normalizeTargetRef(devopsChampion) as string,
          role: 'DevOps Champion',
        });
      }
      if (userList&&userList?.length > 0) {
       
        for (const member of championslist) {
           const isFound=hasSomeDataIssues(member,userList)
            if (isFound) console.log(`Found: ${member}`);
            else hasDataIsh?.(true);
          }

        setTeamChampions(championslist);
        setLoading(prev => ({
          ...prev,
          isChampionsLoading: false,
        }));
      } else {
        setTeamChampions(championslist);
        setLoading(prev => ({
          ...prev,
          isChampionsLoading: false,
        }));
      }
    }
  }

  async function getUserList() {
    const users = (await getCustomEntitiesByKind()).items;
    return users;
  }

  const fetchRoles = useCallback(async () => {
    const groupInfoName = groupInfo?.split('/')[1];

    if (!groupInfoName) {
      onTeamTopologyStatusChange?.('missing');
      dispatch(setTeamType(''));
      setLoading({ isChampionsLoading: false, isMembersLoading: false });
      return;
    }

    try {
      const userList = await getUserList();
      setCatalogUsers(Array.isArray(userList) ? userList : []);
      const data = await fetchData(
        `${urls.getGroupInfoByTeam}${encodeURIComponent(groupInfoName)}`,
      );

      if (data && entity) {
      
      if (data) {
        const processData =data
        

        const teamTopology = processData?.spec?.teamTopology;
        const normalizedTeamTopology =
          typeof teamTopology === 'string' ? teamTopology.trim() : '';

        if (normalizedTeamTopology.length > 0) {
          onTeamTopologyStatusChange?.('defined');
          dispatch(setTeamType(teamTopology as string));
        } else {
          onTeamTopologyStatusChange?.('missing');
          dispatch(setTeamType(''));
        }

        // get all members from the said groupinfo data and add to the current groupmembers
        const members =
          processData?.spec?.userRoles
            ?.filter((item: any) => item?.link)
            ?.map((item: any) => {
              return {
                targetRef: item.link,
                type: 'hasMember',
                source: 'People App',
              } as MemberListProps;
            }) ?? [];
        const uniqueCore = entity.relations
          ?.filter(x => x.type === 'hasMember' && x?.targetRef)
          ?.map(
            item =>
              ({
                ...item,
                source: 'Active Directory',
              }) as MemberListProps,
          );
        const mergedList = [...(members ?? []), ...(uniqueCore ?? [])];

        const combinedMap = mergedList.reduce((acc, item) => {
          const key = item?.targetRef?.toLowerCase();
          if (acc[key]) {
            if (acc[key].source !== item.source) {
              acc[key] = {
                ...acc[key],
                combinedSource: `${acc[key].source} + ${item.source}`,
              };
            }
          } else {
            acc[key] = { ...item };
          }
          return acc;
        }, {});
        const combinedMembers = Object.values(combinedMap).filter(
          (item: any) => item?.targetRef && item?.source,
        );

        const userRoles = processData?.spec?.userRoles as UserRoleProps[];

        if (userRoles) {
          setRoleList(userRoles);
        }
        if (userList?.length > 0) {
          for (const member of combinedMembers) {
             const isFound=hasSomeDataIssues(member as any,userList)
            if (isFound) console.log(`Found: ${member}`);
            else hasDataIsh?.(true);
          }

          setCoreMembers(combinedMembers as any);
        }
        if (!processData?.spec) {
          setLoading(prev => ({
            ...prev,
            isChampionsLoading: false,
          }));
        } else {
          handleChampions(processData,userList);
        }
      } else {
        onTeamTopologyStatusChange?.('missing');
        dispatch(setTeamType(''));
      }
      setLoading(prev => ({
        ...prev,
        isMembersLoading: false,
        isChampionsLoading: false,
      }));
      }
    } catch {
      onTeamTopologyStatusChange?.('missing');
      dispatch(setTeamType(''));
      setCoreMembers([]);
      setTeamChampions([]);
    } finally {
      setLoading({ isChampionsLoading: false, isMembersLoading: false });
    }
  }, [dispatch, entity, groupInfo, onTeamTopologyStatusChange]);

  useEffect(() => {
    if (entity) {
      fetchRoles();
    }
  }, [entity]);

  useEffect(() => {
    const getContributorMembers = async () => {
      try {
        const name = entity.metadata.name.concat('_contributors');
        const response = await fetchData(
          `${urls.getContributorsByTeam}${name}`,
        );
        const data = response.relations?.filter(
          (x: MemberListProps) => x.type === 'hasMember',
        );
        setGroupContributorList(data);
      } catch (exception) {
        if (exception instanceof Error) {
        }
      }
    };
    if (memberType === 'contributor') {
      getContributorMembers();
    }
  }, [memberType, entity.metadata.name]);

  return (
    <>
      <Card className={memberType === 'core' ? classes.card : ''}>
        <div className={classes.cardHeader}>
          <CardHeader
            title={`${memberType === 'core' ? `Core Members (${(coreMembers && coreMembers?.length) || 0})` : memberType === 'contributor' ? `Contributor Members (${groupContributorList?.length ?? 0})` : `Champions (${teamChampions?.length ?? 0})`} `}
          />
          {memberType === 'contributor' && (
            <Legend dataSource="Active Directory" />
          )}
          {memberType === 'core' && <Legend dataSource=" Both " />}
          {memberType === 'champions' && (
            <Legend dataSource="People App" />
          )}
        </div>
        <br />
        <Divider />
        {memberType === 'champions' && loading.isChampionsLoading && (
          <LinearProgress />
        )}
        {memberType === 'core' && loading.isMembersLoading && (
          <LinearProgress />
        )}

        <CardContent className={classes.cardContent}>
          {memberType === 'champions' &&
            !loading.isChampionsLoading &&
            teamChampions?.map(x => {
              return (
                <React.Fragment key={x.targetRef}>
                  <DisplayCardList
                    key={x.role}
                    role={x.role}
                    targetRef={x.targetRef}
                      entityValue={resolveMemberEntity(x.targetRef, catalogUsers)}
                    type="champions"
                  />
                </React.Fragment>
              );
            })}

          {memberType === 'core' &&
            !loading.isMembersLoading &&
            coreMembers &&
            coreMembers?.length > 0 &&
            coreMembers

              ?.filter((item, index, self) => {
                return (
                  self.findIndex(
                    _item => _item.targetRef === item.targetRef,
                  ) === index
                );
              })
              ?.map(x => {
                const memberRole = roleList.find(
                  y => y?.link?.toLowerCase() === x?.targetRef?.toLowerCase(),
                );
                return (
                  <React.Fragment key={x?.targetRef}>
                    <DisplayCardList
                      key={x?.targetRef}
                      source={x?.combinedSource ?? x.source}
                      targetRef={x?.targetRef?.replaceAll('@', '_')}
                      role={memberRole?.role}
                      entityValue={resolveMemberEntity(
                        x?.targetRef?.replaceAll('@', '_'),
                        catalogUsers,
                      )}
                      type="core"
                    />
                  </React.Fragment>
                );
              })}

          {memberType === 'contributor' &&
            groupContributorList?.length > 0 &&
            groupContributorList?.map(x => {
              return (
                <React.Fragment key={x?.targetRef}>
                  <DisplayCardList
                    key={x?.targetRef}
                    targetRef={x?.targetRef}
                    role={x?.role!!}
                      entityValue={resolveMemberEntity(x?.targetRef, catalogUsers)}
                    type="contributor"
                  />
                </React.Fragment>
              );
            })}
        </CardContent>
      </Card>
    </>
  );
};

export default MembersList;
